import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { TellentNavigationItemBadge } from '@core/tellent-types';
import { BadgeComponent, IconComponent } from '@core/user-interface-legacy';

@Component({
  standalone: true,
  selector: 'tlc-navigation-label',
  styleUrls: ['./navigation-label.component.less'],
  host: {
    '[class.desktop-minimized]': 'minimized()',
    '[class.desktop-full]': '!minimized()',
  },
  template: `
    @let iconVal = icon();
    @let iconSvgVal = safeIconSvg();
    @let minimizedVal = minimized();
    @let badgesVal = safeBadges();
    @let showUpgradeIconVal = showUpgradeIcon();

    @if (iconSvgVal) {
      <div class="icon icon--inline-svg" [innerHTML]="iconSvgVal"></div>
    } @else if (iconVal) {
      <div class="icon">
        <rt-icon [name]="$any(iconVal)" />
      </div>
    }

    @if (!minimizedVal) {
      <div class="label">{{ label() }}</div>
    }

    @if (badgesVal.length) {
      <div class="badges">
        @for (badge of badgesVal; track 'label') {
          <rt-badge [color]="badge.color" [text]="badge.text" />
        }
      </div>
    }

    @if (!minimizedVal && showUpgradeIconVal) {
      <div class="upgrade-icon">
        <rt-icon name="upgrade" />
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BadgeComponent, IconComponent],
})
export class TellentNavigationLabelComponent {
  #sanitizer = inject(DomSanitizer);

  public label = input.required<string>();
  public icon = input<string | null>(null);
  public iconSvg = input<string | null>(null);
  public minimized = input<boolean>(false);
  public badges = input<TellentNavigationItemBadge[]>([]);
  public showUpgradeIcon = input<boolean>();

  public safeBadges = computed(() => {
    const badges = this.badges();
    const minimized = this.minimized();

    const _badges = minimized ? badges.slice(0, 1) : badges;

    return _badges.map(badge => {
      const maybeNumber = +badge.text;
      const isNumberBadge = Number.isInteger(maybeNumber);

      if (isNumberBadge) {
        if (minimized) {
          return {
            ...badge,
            text: maybeNumber > 9 ? '9+' : `${maybeNumber}`,
          };
        }

        return {
          ...badge,
          text: maybeNumber > 99 ? '99+' : `${maybeNumber}`,
        };
      }

      return badge;
    });
  });

  public safeIconSvg = computed(() => {
    const svg = this.iconSvg();
    // eslint-disable-next-line ban/ban
    if (svg) return this.#sanitizer.bypassSecurityTrustHtml(svg);

    return null;
  });
}
